// Radha — a reusable "AI proposals awaiting decision" block for any module.
//
// Every module has a *_proposals table with the same decision contract (028):
// draft -> approved|rejected, stamped by a DB trigger, tenant-scoped by RLS.
// This one component reads the drafts and renders Approve/Reject for any of them,
// so a panel gains the write path with a single line:
//     <ModuleProposals table="dispute_proposals" />
// The summary text field varies per module (recommended_action / rationale), so
// it selects * and shows whichever is present.
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { writeErrorMessage } from "../hooks/useCollectionsWrites";
import { useRole } from "../lib/authSupabase";
import { PeacockEye } from "./DevotionalMotifs";

interface Proposal {
  id: string;
  status: string;
  tier: number;
  confidence: string;
  signal_summary?: string;
  recommended_action?: string;
  rationale?: string;
  [k: string]: unknown;
}

function useModuleProposals(table: string) {
  return useQuery({
    queryKey: ["module-proposals", table],
    queryFn: async (): Promise<Proposal[]> => {
      const { data, error } = await supabase
        .from(table)
        .select("*")
        .eq("status", "draft")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as unknown as Proposal[];
    },
    staleTime: 30_000,
  });
}

function useDecide(table: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: "approved" | "rejected" }) => {
      const { data, error } = await supabase
        .from(table)
        .update({ status: decision })
        .eq("id", id)
        .select("id, status")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["module-proposals", table] }),
  });
}

const btn = (bg: string): React.CSSProperties => ({
  padding: "6px 13px", fontSize: 13, color: "#fff", background: bg,
  border: 0, borderRadius: 8, cursor: "pointer",
});
const ghost: React.CSSProperties = {
  padding: "6px 13px", fontSize: 13, color: "var(--fg-muted)", background: "none",
  border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer",
};

function Card({ p, table }: { p: Proposal; table: string }) {
  const decide = useDecide(table);
  const { canWrite } = useRole();
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const headline = p.recommended_action || p.rationale || p.signal_summary || "AI proposal";

  const onDecide = (decision: "approved" | "rejected") =>
    decide.mutate({ id: p.id, decision }, {
      onSuccess: () => setNote({ ok: true, text: `Proposal ${decision}` }),
      onError: (e) => setNote({ ok: false, text: writeErrorMessage(e) }),
    });

  return (
    <div className="sanctuary-card sanctuary-card-interactive sanctuary-reveal" style={{ padding: "13px 16px", borderRadius: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--accent-purple)", fontSize: 12 }}>
        <PeacockEye size={13} />
        <span>AI · {p.tier === 1 ? "LLM" : "rule"} · {Math.round(Number(p.confidence) * 100)}% confidence</span>
      </div>
      <div style={{ fontSize: 13.5, margin: "6px 0", lineHeight: 1.5 }}>{headline}</div>
      {p.signal_summary && p.signal_summary !== headline && (
        <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>{p.signal_summary}</div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
        {canWrite ? (
          <>
            <button className="sanctuary-btn" style={btn("var(--status-good)")} disabled={decide.isPending} onClick={() => onDecide("approved")}>Approve</button>
            <button className="sanctuary-btn" style={ghost} disabled={decide.isPending} onClick={() => onDecide("rejected")}>Reject</button>
          </>
        ) : (
          <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>Read-only</span>
        )}
        {note && (
          <span style={{ marginLeft: "auto", fontSize: 13, color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>{note.text}</span>
        )}
      </div>
    </div>
  );
}

export function ModuleProposals({ table }: { table: string }) {
  const { data, isLoading } = useModuleProposals(table);
  const drafts = data ?? [];
  if (isLoading || drafts.length === 0) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      <div className="font-devotional-copy" style={{ font: "italic 600 15px \"Cormorant Garamond\"", color: "var(--accent-purple)", margin: "0 0 8px" }}>
        AI proposals awaiting your decision ({drafts.length})
      </div>
      <div className="sanctuary-stagger" style={{ display: "grid", gap: 10 }}>
        {drafts.map((p) => <Card key={p.id} p={p} table={table} />)}
      </div>
    </div>
  );
}
